import React, { useEffect, useState } from "react";
import "../../../../views/print/receipt.scss";
import { getDecryptedToken } from "../../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../../utils/currency/currencyUtils";
import Amount from "../../../../views/print/Amount";
import baseUrl from "../../../../api/baseUrl";
import { getMediaUrl } from "../../../../utils/mediaUrl";

// Map currency symbols to time zones
const currencyTimeZoneMap: Record<string, string> = {
  $: "America/New_York",
  "¥": "Asia/Tokyo",
  "Rs.": "Asia/Colombo",
  "€": "Europe/Berlin",
  "£": "Europe/London",
  "₹": "Asia/Kolkata",
  A$: "Australia/Sydney",
  C$: "America/Toronto",
};

// Helper function to format phone number by removing country code
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return "";
  let cleaned = phoneNumber.replace(/[\s\-()]/g, "");
  if (cleaned.startsWith("+")) {
    cleaned = cleaned.substring(1);
  }
  const countryCodePatterns = [
    { code: "94", replace: "0" },
    { code: "91", replace: "0" },
    { code: "1", replace: "" },
    { code: "44", replace: "0" },
    { code: "61", replace: "0" },
    { code: "81", replace: "0" },
    { code: "86", replace: "0" },
    { code: "49", replace: "0" },
    { code: "33", replace: "0" },
    { code: "39", replace: "0" },
    { code: "971", replace: "0" },
    { code: "966", replace: "0" },
    { code: "65", replace: "" },
    { code: "60", replace: "0" },
  ];
  for (const pattern of countryCodePatterns) {
    if (cleaned.startsWith(pattern.code)) {
      return pattern.replace + cleaned.substring(pattern.code.length);
    }
  }
  if (!cleaned.startsWith("0") && cleaned.length >= 9) {
    return "0" + cleaned;
  }
  return cleaned;
};

// Format number to 2 decimal places
const formatNumber = (value: any) => Number(value || 0).toFixed(2);

function ReceiptComponent({
  selectedBill,
  finalProducts,
  finalPayments,
}: {
  selectedBill: any;
  finalProducts: any[];
  finalPayments?: any;
}) {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!selectedBill) return null;

  const payments = finalPayments || selectedBill.payments || {};
  const products = finalProducts || selectedBill.products || [];

  const shopName = getDecryptedToken("martName");
  const address = getDecryptedToken("address");
  const phoneNumber = getDecryptedToken("phoneNumber");
  const name = getDecryptedToken("name");
  const email = getDecryptedToken("email");
  const isEmailShown = getDecryptedToken("isEmailShown") === "true";
  const registrationNumber = getDecryptedToken("registrationNumber");
  const logo = getDecryptedToken("logo");
  const currency = getDecryptedToken("currency");
  const currencySymbol = getCurrencySymbol();

  const formattedPhoneNumber = formatPhoneNumber(phoneNumber || "");
  const addressNumberMatch = address?.match(/(T\d{13,})/);
  const addressNumber = addressNumberMatch ? addressNumberMatch[1] : null;

  const timeZone =
    currencyTimeZoneMap[currency as string] ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const applyInline =
    typeof shopName === "string" &&
    (shopName.trim().toLowerCase() === "mmk chiba" ||
      shopName.trim().toLowerCase() === "ayman bakery restaurant" ||
      shopName.trim().toLowerCase() === "world trading asian food");

  // Check if it's a credit sale
  const isCredit = payments?.isCreditSale || false;
  const unpaidAmount = Number(payments?.unpaidAmount || 0);
  const discountAmount = Number(payments?.discount || 0);
  const billDiscountAmount = Number(payments?.givenDiscount || 0);
  const displayNetTotal = Math.max(
    Number(payments?.netTotal || 0) - billDiscountAmount,
    0,
  );

  // Create amount array similar to Receipt.tsx
  const amount = [
    { value: payments.total, name: "total" },
    { value: discountAmount, name: "discount" },
    { value: billDiscountAmount, name: "bill discount" },
    { value: payments.tax || 0, name: "tax" },
    { value: displayNetTotal, name: "nettotal" },
    ...(Number(payments.cash) > 0
      ? [{ value: payments.cash, name: "cash" }]
      : []),
    ...(Number(payments.card) > 0
      ? [{ value: payments.card, name: "card" }]
      : []),
    ...(Number(payments.bank) > 0
      ? [{ value: payments.bank, name: "bank" }]
      : []),
    { value: payments.amountRecived, name: "totalPay" },
    isCredit && unpaidAmount > 0
      ? { value: unpaidAmount, name: "pending payment" }
      : { value: payments.balance, name: "balance" },
  ];

  return (
    <div className={`receipt${applyInline ? " mmk-chiba" : ""}`}>
      <div className="headers">
        {logo && logo !== "" && (
          <div className="logo">
            <img
              src={getMediaUrl(logo)}
              alt="Shop Logo"
              style={{
                width: "80px",
                height: "80px",
                objectFit: "contain",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                padding: "4px",
                marginBottom: "10px",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}
        <div className="title">{shopName}</div>
        <div className="address">{address}</div>
        {addressNumber && <div className="address-number">{addressNumber}</div>}
        {registrationNumber && (
          <div className="registration-number">
            Reg No: {registrationNumber}
          </div>
        )}
        <div className="tell">{formattedPhoneNumber}</div>
        {isEmailShown && email && <div className="email">{email}</div>}
        <div className="bilNUmber">{payments.billNumber}</div>
        {isCredit && <div className="sale-type credit">CREDIT SALE</div>}
        <div className="time_r">
          <div className="date_r">
            {currentTime.toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "2-digit",
              timeZone,
            }) +
              " " +
              currentTime.toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                timeZone,
              })}
          </div>
          <div className="name_r">{name}</div>
        </div>
      </div>

      <hr />

      <div className="heading">
        <div className="row1">
          <span>Ln</span>
          <span>items</span>
        </div>
        <div className="row2">
          <span>qty</span>
          <span>rate</span>
          <span>gross</span>
          <span>dis%</span>
          <span>net amt</span>
        </div>
      </div>

      <hr />

      {products?.map((item: any, i: number) => {
        const itemName =
          item?.Item?.name ||
          item?.itemName ||
          item?.name ||
          item?.["Item.name"] ||
          "Item";

        return (
          <div className="r_items" key={i}>
            <div className="row1">
              <span>{String(i + 1).padStart(2, "0")}.</span>
              <span>{itemName}</span>
            </div>
            <div className="row2">
              <span>{item.quantity}</span>
              <span>
                {currencySymbol} {formatNumber(item.price)}
              </span>
              <span>X</span>
              <span>{formatNumber(item.lineDiscount || 0)}</span>
              <span>
                {currencySymbol} {formatNumber(item.price * item.quantity)}
              </span>
            </div>
          </div>
        );
      })}

      <hr />

      <Amount
        amount={amount.map((a) => ({
          ...a,
          value: `${currencySymbol} ${formatNumber(a.value)}`,
        }))}
      />

      <hr />

      {isCredit && unpaidAmount > 0 && (
        <div className="credit-notice">
          <p>
            Amount pending: {currencySymbol} {formatNumber(unpaidAmount)}
          </p>
          <p>Please settle your account soon.</p>
        </div>
      )}

      <div className="thank">Thank You! Come again</div>
    </div>
  );
}

export default ReceiptComponent;
